import React, { useEffect, useState } from "react";
import { connect } from "dva";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  Switch,
  Row,
  Col,
  Divider,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { StoreState } from "@src/interfaces";
import Loading from "@src/components/Loading";
import { helper } from "@src/controls/controlHelper";
import HttpStatusCode from "@src/constants/HttpStatusCode";
import clone from "lodash/clone";

const { TextArea } = Input;
const { Option } = Select;

// Các loại câu hỏi khảo sát
export enum QuestionType {
  TEXT = "text",
  TEXTAREA = "textarea",
  RADIO = "radio",
  CHECKBOX = "checkbox",
  RATING = "rating",
  DROPDOWN = "dropdown",
}

export const QuestionTypeLabels: Record<QuestionType, string> = {
  [QuestionType.TEXT]: "Trả lời ngắn",
  [QuestionType.TEXTAREA]: "Trả lời dài",
  [QuestionType.RADIO]: "Chọn 1",
  [QuestionType.CHECKBOX]: "Chọn nhiều",
  [QuestionType.RATING]: "Đánh giá (1-5)",
  [QuestionType.DROPDOWN]: "Danh sách chọn",
};

interface SurveyFormCtrlProps {
  query?: any;
  onClose?: () => void;
  pageInfo?: any;
  authUser?: any;
}

const SurveyQuestionFormCtrl: React.FC<SurveyFormCtrlProps> = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageInfo, setPageInfo] = useState<any>(null);

  const query = props?.query;
  const mode = query?.mode || "create";
  const id = query?.id;
  const pageId = query?.page;

  // Load page info và data nếu edit
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        let _pageInfo = props.pageInfo;
        if (!_pageInfo && pageId) {
          _pageInfo = await helper.getPage(pageId);
        }
        setPageInfo(_pageInfo);

        // Load data nếu mode edit
        if (mode === "edit" && id && _pageInfo) {
          const rs: any = await helper.callPageApi(_pageInfo, _pageInfo.read, {
            queryInput: JSON.stringify({ id }),
          });
          if (rs.status === HttpStatusCode.OK) {
            const data = rs?.data?.data?.[0] ?? {};

            // Parse questions từ JSON nếu cần
            if (typeof data.questions === "string") {
              data.questions = JSON.parse(data.questions);
            }

            // Unpack config của từng question thành các field riêng để form hiển thị
            if (Array.isArray(data.questions)) {
              data.questions = data.questions.map((q: any) => {
                const config =
                  typeof q.config === "string"
                    ? JSON.parse(q.config)
                    : q.config || {};

                return {
                  ...q,
                  // Unpack config thành các field riêng cho form
                  options: config.options || [],
                  hasOther: config.hasOther || false,
                };
              });
            }

            console.log("Edit data loaded:", data);
            form.setFieldsValue(data);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, mode, id, form, props.pageInfo]);

  // Submit form
  const onFinish = async (values: any) => {
    console.log("🚀 ~ onFinish ~ values:", values);

    setLoading(true);
    try {
      if (!pageInfo) {
        helper.alert("Không tìm thấy thông tin trang", "error");
        return;
      }

      const data = clone(values);

      // Xử lý questions - đóng gói config cho từng câu hỏi
      if (data.questions && Array.isArray(data.questions)) {
        data.questions = data.questions.map((q: any, index: number) => {
          const config: any = {};

          if (
            [
              QuestionType.RADIO,
              QuestionType.CHECKBOX,
              QuestionType.DROPDOWN,
            ].includes(q.type)
          ) {
            config.options = q.options || [];
            config.hasOther = q.hasOther || false;
          }

          return {
            title: q.title,
            description: q.description,
            type: q.type,
            required: q.required || false,
            order: index,
            config: Object.keys(config).length > 0 ? config : null,
          };
        });
      }

      // API name là "create" hoặc "update" - lấy trực tiếp từ apis array
      const apiName = mode === "edit" ? "update" : "create";

      // Thêm id khi edit
      if (mode === "edit" && id) {
        data.id = id;
      }

      const response: any = await helper.callPageApi(pageInfo, apiName, data);

      if (response.status === HttpStatusCode.OK && !response?.data?.errorCode) {
        helper.alert(response?.data?.message || "Thành công", "success");
        if (props.onClose) {
          props.onClose();
        } else {
          window.history.back();
        }
      } else {
        helper.alert(response?.data?.message || "Đã có lỗi xảy ra", "error");
      }
    } catch (error: any) {
      helper.alert(error.message || "Đã có lỗi xảy ra", "error");
    } finally {
      setLoading(false);
    }
  };

  // Component render options cho câu hỏi
  const QuestionOptions: React.FC<{ fieldName: number }> = ({ fieldName }) => (
    <Card size="small" style={{ marginTop: 8, background: "#fafafa" }}>
      <Form.List name={[fieldName, "options"]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => (
              <Row
                key={key}
                gutter={8}
                align="middle"
                style={{ marginBottom: 8 }}
              >
                <Col flex="auto">
                  <Form.Item
                    {...restField}
                    name={[name, "label"]}
                    rules={[{ required: true, message: "Nhập lựa chọn" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder={`Lựa chọn ${index + 1}`} />
                  </Form.Item>
                </Col>
                <Col>
                  <MinusCircleOutlined
                    onClick={() => remove(name)}
                    style={{ color: "#ff4d4f" }}
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              size="small"
            >
              Thêm lựa chọn
            </Button>
          </>
        )}
      </Form.List>
      <Row align="middle" style={{ marginTop: 8, marginBottom: 0 }}>
        <Col>
          <Form.Item
            name={[fieldName, "hasOther"]}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch size="small" />
          </Form.Item>
        </Col>
        <Col>
          <span style={{ marginLeft: 8, fontSize: 12 }}>
            Cho phép nhập "Khác"
          </span>
        </Col>
      </Row>
    </Card>
  );

  // Component render 1 câu hỏi
  const QuestionItem: React.FC<{
    field: any;
    index: number;
    remove: (index: number) => void;
  }> = ({ field, index, remove }) => {
    return (
      <Card
        size="small"
        style={{ marginBottom: 16, padding: "0 16px" }}
        title={`Câu hỏi ${index + 1}`}
        extra={
          <DeleteOutlined
            onClick={() => remove(field.name)}
            style={{ color: "#ff4d4f", cursor: "pointer" }}
          />
        }
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name={[field.name, "title"]}
              label="Tiêu đề"
              rules={[{ required: true, message: "Nhập tiêu đề câu hỏi" }]}
            >
              <Input placeholder="Nhập câu hỏi" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={[field.name, "type"]}
              label="Loại"
              rules={[{ required: true, message: "Chọn loại" }]}
              initialValue={QuestionType.TEXT}
            >
              <Select>
                {Object.entries(QuestionTypeLabels).map(([key, label]) => (
                  <Option key={key} value={key}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name={[field.name, "description"]} label="Mô tả">
          <Input placeholder="Mô tả thêm (tùy chọn)" />
        </Form.Item>

        <Row align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Form.Item
              name={[field.name, "required"]}
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col>
            <span style={{ marginLeft: 8 }}>Bắt buộc trả lời</span>
          </Col>
        </Row>

        {/* Render config dựa trên loại câu hỏi - dùng shouldUpdate */}
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            const prevType = prevValues?.questions?.[field.name]?.type;
            const currType = currentValues?.questions?.[field.name]?.type;
            return prevType !== currType;
          }}
        >
          {({ getFieldValue }) => {
            const questionType = getFieldValue([
              "questions",
              field.name,
              "type",
            ]);
            return (
              <>
                {[
                  QuestionType.RADIO,
                  QuestionType.CHECKBOX,
                  QuestionType.DROPDOWN,
                ].includes(questionType) && (
                  <QuestionOptions fieldName={field.name} />
                )}
              </>
            );
          }}
        </Form.Item>
      </Card>
    );
  };

  if (loading && !pageInfo) {
    return <Loading />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        questions: [{ type: QuestionType.TEXT }],
      }}
    >
      <Card title={mode === "edit" ? "Chỉnh sửa khảo sát" : "Tạo khảo sát mới"}>
        {/* Thông tin khảo sát */}
        <Form.Item
          name="title"
          label="Tên khảo sát"
          rules={[{ required: true, message: "Nhập tên khảo sát" }]}
        >
          <Input placeholder="VD: Khảo sát CLB Thể Công - Viettel năm 2026" />
        </Form.Item>

        <Form.Item name="description" label="Mô tả khảo sát">
          <TextArea rows={2} placeholder="Mô tả ngắn về khảo sát" />
        </Form.Item>

        <Divider>Danh sách câu hỏi</Divider>

        {/* Danh sách câu hỏi động */}
        <Form.List name="questions">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <QuestionItem
                  key={field.key}
                  field={field}
                  index={index}
                  remove={remove}
                />
              ))}
              <Button
                type="dashed"
                onClick={() => add({ type: QuestionType.TEXT })}
                block
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
              >
                Thêm câu hỏi
              </Button>
            </>
          )}
        </Form.List>

        <Divider />

        {/* Buttons */}
        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === "edit" ? "Cập nhật" : "Tạo khảo sát"}
            </Button>
            <Button
              onClick={() => {
                if (props.onClose) {
                  props.onClose();
                } else {
                  window.history.back();
                }
              }}
            >
              Hủy
            </Button>
          </Space>
        </Form.Item>
      </Card>
    </Form>
  );
};

const mapStateToProps = ({ auth }: StoreState) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps)(SurveyQuestionFormCtrl);
